<template>
  <div class="page-layout">
    <Sidebar />
    <div class="profile-container">
      <div class="profile-header">
        <h1>My Profile</h1>
      </div>

      <div class="profile-content" v-if="profile">
        <div class="profile-card">
          <div class="profile-image">
            <img :src="profile.profile_url" :alt="fullName" />
          </div>
          
          <div class="profile-info">
            <h2>
              <div class="editable-field">
                <input v-if="editingField === 'first_name'" 
                       v-model="editValue" 
                       @blur="saveField('first_name')"
                       @keyup.enter="saveField('first_name')"
                       ref="firstNameInput"
                       placeholder="First Name" />
                <input v-if="editingField === 'last_name'"
                       v-model="editValue"
                       @blur="saveField('last_name')"
                       @keyup.enter="saveField('last_name')"
                       ref="lastNameInput"
                       placeholder="Last Name" />
                <template v-if="!editingField">
                  <span @click="startEdit('first_name', profile.first_name)">{{ profile.first_name }}</span>
                  <span @click="startEdit('last_name', profile.last_name)">{{ profile.last_name }}</span>
                </template>
                <span v-if="editingField" class="save-indicator">Press Enter to save</span>
              </div>
            </h2>
            <p class="title">{{ profile.title }}</p>
            
            <div class="info-grid">
              <div class="info-item">
                <label>Email</label>
                <div class="editable-field">
                  <input v-if="editingField === 'email'"
                         v-model="editValue"
                         @blur="saveField('email')"
                         @keyup.enter="saveField('email')"
                         ref="emailInput"
                         type="email"
                         placeholder="Email" />
                  <p v-else @click="startEdit('email', profile.email)">{{ profile.email }}</p>
                </div>
              </div>
              
              <div class="info-item">
                <label>Last Login</label>
                <p>{{ formatDate(profile.last_login_at) }}</p>
              </div>
              
              <div class="info-item">
                <label>Member Since</label>
                <p>{{ formatDate(profile.created_at) }}</p>
              </div>
              
              <div class="info-item">
                <label>Phone Number</label>
                <div class="editable-field">
                  <input v-if="editingField === 'phone_number'"
                         v-model="editValue"
                         @blur="saveField('phone_number')"
                         @keyup.enter="saveField('phone_number')"
                         ref="phoneInput"
                         placeholder="Phone Number" />
                  <p v-else @click="startEdit('phone_number', profile.phone_number || '')">
                    {{ profile.phone_number || 'Not provided' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bio-card">
          <h3>Biography</h3>
          <div class="editable-field">
            <textarea v-if="editingField === 'bio'"
                     v-model="editValue"
                     @blur="saveField('bio')"
                     @keyup.enter="saveField('bio')"
                     ref="bioInput"
                     placeholder="Write your biography here"></textarea>
            <p v-else @click="startEdit('bio', profile.bio)">{{ profile.bio }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else class="loading">
        <svg class="spinner" viewBox="0 0 24 24">
          <circle class="spinner-circle" cx="12" cy="12" r="10" />
        </svg>
        Loading profile...
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted, nextTick } from 'vue';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'UserProfile',
  components: {
    Sidebar
  },
  setup() {
    const profile = ref(null);
    const error = ref(null);
    const editingField = ref(null);
    const editValue = ref('');
    const updateError = ref(null);

    // Create refs for input elements
    const firstNameInput = ref(null);
    const lastNameInput = ref(null);
    const emailInput = ref(null);
    const phoneInput = ref(null);
    const bioInput = ref(null);

    const fullName = computed(() => {
      if (!profile.value) return '';
      return profile.value.first_name + ' ' + profile.value.last_name;
    });

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const startEdit = async (field, value) => {
      editingField.value = field;
      editValue.value = value;
      await nextTick();
      
      // Focus the input after it's rendered using the correct ref
      const inputRefs = {
        first_name: firstNameInput,
        last_name: lastNameInput,
        email: emailInput,
        phone_number: phoneInput,
        bio: bioInput
      };
      
      if (inputRefs[field]?.value) {
        inputRefs[field].value.focus();
      }
    };

    const saveField = async (field) => {
      if (!editValue.value.trim()) {
        editingField.value = null;
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.put('https://api.kewaar.com/api/admin/profile', 
          { [field]: editValue.value },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          profile.value = { ...profile.value, [field]: editValue.value };
          updateError.value = null;
        } else {
          throw new Error(response.data.message || 'Failed to update profile');
        }
      } catch (err) {
        updateError.value = err.response?.data?.message || err.message || 'Failed to update profile';
        console.error('Profile update error:', err);
      } finally {
        editingField.value = null;
        editValue.value = '';
      }
    };

    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get('https://api.kewaar.com/api/admin/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          profile.value = response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to fetch profile');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || 'Failed to load profile';
        console.error('Profile fetch error:', err);
      }
    };

    onMounted(fetchProfile);

    return {
      profile,
      error,
      updateError,
      fullName,
      formatDate,
      editingField,
      editValue,
      startEdit,
      saveField,
      // Return the refs
      firstNameInput,
      lastNameInput,
      emailInput,
      phoneInput,
      bioInput
    };
  }
};
</script>

<style scoped>
.page-layout {
  display: flex;
  min-height: 100vh;
}

.profile-container {
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-left: 280px; /* Match sidebar width */
}

.profile-header {
  margin-bottom: 2rem;
}

.profile-header h1 {
  color: #14171F;
  font-size: 2rem;
  font-weight: 600;
}

.profile-content {
  display: grid;
  gap: 2rem;
}

.profile-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(20, 23, 31, 0.06);
  display: flex;
  gap: 2rem;
}

.profile-image {
  flex-shrink: 0;
}

.profile-image img {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  object-fit: cover;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #14171F;
  margin: 0 0 0.5rem 0;
}

.title {
  color: #4a5568;
  font-size: 1.1rem;
  margin: 0 0 1.5rem 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.info-item label {
  display: block;
  color: #718096;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.info-item p {
  color: #14171F;
  font-size: 1rem;
  margin: 0;
}

.bio-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(20, 23, 31, 0.06);
}

.bio-card h3 {
  color: #14171F;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
}

.bio-card p {
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
}

.error-message {
  background-color: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.2);
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #4a5568;
  padding: 2rem;
}

.spinner {
  width: 24px;
  height: 24px;
  animation: rotate 1s linear infinite;
}

.spinner-circle {
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-dasharray: 60;
  stroke-dashoffset: 45;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dashoffset: 60;
  }
  50% {
    stroke-dashoffset: 30;
  }
  100% {
    stroke-dashoffset: 60;
  }
}

@media (max-width: 768px) {
  .profile-container {
    margin-left: 0;
    padding: 1rem;
  }

  .profile-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .profile-image img {
    width: 150px;
    height: 150px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    text-align: left;
  }
}

.editable-field {
  position: relative;
  cursor: pointer;
}

.editable-field input,
.editable-field textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: inherit;
  font-family: inherit;
  background: white;
  color: #14171F;
}

.editable-field input:focus,
.editable-field textarea:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
}

.editable-field textarea {
  min-height: 100px;
  resize: vertical;
}

.save-indicator {
  position: absolute;
  right: 0;
  top: 100%;
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.editable-field p:hover,
.editable-field span:hover {
  background: rgba(66, 153, 225, 0.1);
  border-radius: 4px;
}
</style> 